import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  OpenInNewOutlined,
  VisibilityOffOutlined,
  ThumbUpOutlined,
  ChatBubbleOutlineOutlined,
} from "@mui/icons-material";
import { MentionResult } from "../types";
import PlatformIcon from "./PlatformIcon";
import {
  MENTION_STATUS_NEW,
  MENTION_STATUS_SEEN,
  MENTION_STATUS_DISMISSED,
} from "../../../constants";
import moment from "moment";

interface MentionCardProps {
  mention: MentionResult;
  onClick: (mention: MentionResult) => void;
  onDismiss: (mention: MentionResult) => void;
}

const MentionCard: React.FC<MentionCardProps> = ({
  mention,
  onClick,
  onDismiss,
}) => {
  const getStatusColor = () => {
    switch (mention.status) {
      case MENTION_STATUS_NEW:
        return "primary";
      case MENTION_STATUS_SEEN:
        return "default";
      case MENTION_STATUS_DISMISSED:
        return "default";
      default:
        return "default";
    }
  };

  const getSentimentColor = () => {
    switch (mention.sentiment) {
      case "positive":
        return "success";
      case "negative":
        return "error";
      default:
        return "default";
    }
  };

  const handleDismiss = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDismiss(mention);
  };

  const handleOpenInNew = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(mention.postUrl, "_blank");
  };

  return (
    <Card
      sx={{
        cursor: "pointer",
        transition: "box-shadow 0.2s",
        borderLeft:
          mention.status === MENTION_STATUS_NEW
            ? "3px solid"
            : "3px solid transparent",
        borderLeftColor:
          mention.status === MENTION_STATUS_NEW ? "primary.main" : "transparent",
        opacity: mention.status === MENTION_STATUS_DISMISSED ? 0.6 : 1,
        "&:hover": {
          boxShadow: 4,
        },
      }}
      onClick={() => onClick(mention)}
    >
      <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PlatformIcon platform={mention.platform} size={20} />
            {mention.subreddit && (
              <Typography variant="body2" color="text.secondary">
                r/{mention.subreddit}
              </Typography>
            )}
            <Chip
              size="small"
              label={mention.status}
              color={getStatusColor()}
              sx={{ height: 20, fontSize: "0.7rem", textTransform: "capitalize" }}
            />
            {mention.sentiment && (
              <Chip
                size="small"
                label={mention.sentiment}
                color={getSentimentColor()}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem", textTransform: "capitalize" }}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Open in new tab">
              <IconButton size="small" onClick={handleOpenInNew}>
                <OpenInNewOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            {mention.status !== MENTION_STATUS_DISMISSED && (
              <Tooltip title="Dismiss">
                <IconButton size="small" onClick={handleDismiss}>
                  <VisibilityOffOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            mb: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {mention.title || "(No title)"}
        </Typography>

        {mention.content && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
            }}
          >
            {mention.content}
          </Typography>
        )}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
          {mention.matchedKeywords.map((keyword) => (
            <Chip
              key={keyword}
              label={keyword}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: 22 }}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {mention.metrics.upvotes !== undefined && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ThumbUpOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {mention.metrics.upvotes}
                </Typography>
              </Box>
            )}
            {mention.metrics.points !== undefined && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ThumbUpOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {mention.metrics.points} pts
                </Typography>
              </Box>
            )}
            {mention.metrics.comments !== undefined && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ChatBubbleOutlineOutlined
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {mention.metrics.comments}
                </Typography>
              </Box>
            )}
            {mention.authorUsername && (
              <Typography variant="caption" color="text.secondary">
                by {mention.authorUsername}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {mention.postedAt
              ? moment(mention.postedAt).fromNow()
              : moment(mention.foundAt).fromNow()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MentionCard;
